import * as React from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from '../utils';
import type { UnsafeProps } from '../escape-hatches';

export type IconButtonSize = 'sm' | 'md' | 'lg';
export type IconButtonIntent = 'default' | 'destructive';

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'aria-label'>, UnsafeProps {
  /** Required accessible name — icon-only buttons MUST have a label. */
  ariaLabel: string;
  size?: IconButtonSize;
  intent?: IconButtonIntent;
  round?: boolean;
  /** Toggle mode — sets aria-pressed. */
  pressed?: boolean;
  /** Loading state — shows spinner, disables interaction. */
  loading?: boolean;
  className?: string;
  children: ReactNode;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    children,
    ariaLabel,
    title,
    size = 'md',
    intent = 'default',
    round,
    pressed,
    loading,
    className,
    unsafe_className,
    unsafe_style,
    type = 'button',
    onClick,
    ...rest
  },
  ref,
) {
  const handleClick = loading ? undefined : onClick;

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      aria-label={ariaLabel}
      aria-pressed={pressed != null ? pressed : undefined}
      aria-busy={loading ? true : undefined}
      title={title}
      className={cx(
        'j-icon-btn',
        size !== 'md' && `j-icon-btn-${size}`,
        intent === 'destructive' && 'j-icon-btn-destructive',
        round && 'j-icon-btn-round',
        className,
        unsafe_className,
      )}
      style={unsafe_style}
      onClick={handleClick}
    >
      {loading ? (
        <span className="j-btn-spinner" aria-hidden="true">
          <svg width="1em" height="1em" viewBox="0 0 16 16" fill="none">
            <circle
              cx="8"
              cy="8"
              r="6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="28"
              strokeDashoffset="8"
            />
          </svg>
        </span>
      ) : (
        children
      )}
    </button>
  );
});
